/**
 * @name AppHeaderDropdown
 * @description A dropdown component that provides user account-related options, such as viewing the profile and logging out. It utilizes CoreUI components for the dropdown and Redux for managing user logout functionality.
 * @component
 * @returns {JSX.Element} The rendered dropdown menu with profile and logout options.
 */
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  CAvatar,
  CDropdown,
  CDropdownDivider,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
} from "@coreui/react";
import { cilLockLocked, cilUser, cilAccountLogout } from "@coreui/icons";
import CIcon from "@coreui/icons-react";

import logo from "@/assets/images/avatars/DefaultImg.png";
import { logoutSuccess, clearUser, loggedUser } from "@/store/features/userSlice";
import { setAuthToken } from "@/libs/axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { signIn, profile } from "@/libs/routes";
import { useLogout } from "@/hooks";
import { toast } from "react-toastify";
import { safeDecryptProfileUrl } from "@/helpers/cryptoUtil";

const AppHeaderDropdown = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get user data from Redux store
  const user = useSelector(loggedUser);
  
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Profile data (including the latest profile picture) is already refreshed once
  // on app boot (src/index.js's onBeforeLift) and immediately after login
  // (pages/sign-in/index.js) - both dispatch the same `addUser` shape this component
  // reads from Redux, so no separate fetch is needed here on every mount.

  /**
   * @name handleLogout
   * @description Logs the user out by calling the logout API, dispatching a logout action, 
   * clearing the auth token, and redirecting to the sign-in page.
   * @returns {void}
   */
  const handleLogout = async () => {
    try {
      // Call logout API
      await logoutMutationAsync();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error("Logout API error:", error);
    } finally {
      // Clear user data from Redux
      dispatch(logoutSuccess());
      dispatch(clearUser());
      
      // Clear auth token
      setAuthToken(null);
      
      // Redirect to sign-in page
      navigate(signIn);
    }
  };

  // Logout mutation hook
  const { mutateAsync: logoutMutationAsync } = useLogout(
    (response) => {
      toast.success(response.message || "Logged out successfully");
    },
    (error) => {
      // Handle error silently - we still want to log out locally
      console.error("Logout error:", error);
    }
  );

  // Get profile image from Redux user state and decrypt it safely
  // Check if user exists before accessing profile photo
  const encryptedProfileUrl = user?.user?.profile_photo_url;
  
  const decryptedUrl = safeDecryptProfileUrl(encryptedProfileUrl);
  
  const profileImageUrl = decryptedUrl || logo;

  return (
    <div className="dropdown" ref={dropdownRef}>
      <button
        className="btn dropdown-toggle"
        type="button"
        id="profileDropdown"
        aria-expanded={isDropdownOpen}
        onClick={() => {
          setIsDropdownOpen(!isDropdownOpen);
        }}
        style={{
          background: 'none',
          border: 'none',
          padding: 0
        }}
      >
        <img
          className="header-profile-image rounded-circle"
          src={profileImageUrl}
          alt="Profile"
          style={{
            width: '40px',
            height: '40px',
            objectFit: 'cover',
            border: '2px solid #7C3AED',
            borderRadius: '50%',
            cursor: 'pointer'
          }}
          onError={(e) => {
            e.target.src = logo;
          }}
          onLoad={() => {
          }}
        />
      </button>
      {isDropdownOpen && (
        <ul 
          className="dropdown-menu show pt-0" 
          aria-labelledby="profileDropdown"
          style={{
            position: 'absolute',
            top: '100%',
            right: '0',
            left: 'auto',
            display: 'block',
            zIndex: 1000,
            boxShadow: 'none',
            border: '1px solid #333333',
            borderRadius: '8px',
            overflow: 'hidden',
            background: '#212631'
          }}
        >
          <li><h6 className="dropdown-header bg-body-secondary">Account</h6></li>
          <li>
            <button 
              className="dropdown-item"
              type="button"
              onClick={(e) => {
            
                
                e.preventDefault();
                setIsDropdownOpen(false);
                
                try {
                  navigate('/edit-profile');
                } catch (error) {
                  console.error('Navigation error:', error);
                  window.location.href = '/edit-profile';
                }
              }}
            >
              <CIcon icon={cilUser} className="me-2" />
              Profile
            </button>
          </li>
          <li><hr className="dropdown-divider" /></li>
          <li>
            <button 
              className="dropdown-item"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setIsDropdownOpen(false);
                handleLogout();
              }}
            >
              <CIcon icon={cilAccountLogout} className="me-2" />
              Logout
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

export default AppHeaderDropdown;

